import jwt_decode from "jwt-decode";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { setCookie, deleteCookie } from "@/utils";
import setAuthToken from "@/services/setAuthToken";
import { useDispatch, useSelector } from "@/redux/store";
import { fetchUserDetails } from "@/redux/apps/users/UserSlice";
import { BACKEND_BASE_URL, COMMON_CALLBACK_URL, COMMON_SUB_DOMAIN } from "@/config";
import { showErrorMessage } from "@/redux/apps/toast/ToastSlice";
import { concatAndEncodeBase64, fetchLoggedUser, getSubdomain } from "@/utils/common";
import subDomainClientMapping from "@/config/domainMapping";
import { isSingleProductUser } from "@/utils/helpers";

export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const subDomain = getSubdomain();
  const { redirectURL, domain, clientId, clientSecret } = subDomainClientMapping(subDomain);
  const defaultSubDomain = subDomain !== "" && subDomain !== COMMON_SUB_DOMAIN ? subDomain : "xsm";

  const { loggedUser, loggedUserOrganisations } = useSelector((state) => state.user);
  const ACCESS_TOKEN = localStorage.getItem(`${defaultSubDomain}_access_token`) ?? "";
  const REFRESH_TOKEN = localStorage.getItem(`${defaultSubDomain}_refresh_token`) ?? "";
  const ID_TOKEN = localStorage.getItem(`${defaultSubDomain}_id_token`) ?? "";

  const userName = fetchLoggedUser();
  const hasSingleProduct = useMemo(
    () => isSingleProductUser(loggedUserOrganisations),
    [loggedUserOrganisations]
  );

  async function fetchLoggedTokenInfo(tokenURI: string, domainName: string, adminEmail?: string) {
    if (tokenURI) {
      const decoded: any = jwt_decode(tokenURI as string);
      if (decoded?.sub) {
        await setAuthToken(tokenURI as string);
        // Store token with subdomain-specific key
        localStorage.setItem(`${defaultSubDomain}_access_token`, tokenURI);
        // Also store with generic key for backward compatibility
        localStorage.setItem("xsm_access_token", tokenURI);
        // if (domainName) {
        //   setCookie("xsm_user_domain", domainName);
        // }

        if (!adminEmail) {
          try {
            const response = await dispatch(
              fetchUserDetails({ userName, domainName: domainName ?? domain })
            );
            if (response?.payload && response?.payload?.statusCode === 200) {
              const {
                payload: {
                  result: { users }
                }
              } = response;

              if (users?.length > 0) {
                setCookie("xsm_user_details", JSON.stringify(users[0]));
                const { organizations = [] } = users[0];
                if (!organizations.length) {
                  //redirect to dashboard page
                  navigate("/dashboard");
                }
              }
            }
          } catch (error) {
            console.error(error, "error");
          }
        }
      }
    }
  }

  const revokeToken = async (token: string, tokenType: "access_token" | "refresh_token") => {
    //base64 authorization
    const basicAuthorozationSecret = concatAndEncodeBase64(
      clientId as string,
      clientSecret as string
    );
    try {
      const response = await fetch(`${BACKEND_BASE_URL}/xsm-api/v1/proxy/oauth2/revoke`, {
        method: "POST",
        headers: {
          "XSM-Domain": domain || "",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Basic ${basicAuthorozationSecret}`
        },
        body: new URLSearchParams({
          token,
          token_type_hint: tokenType
        })
      });

      if (!response.ok) {
        throw new Error("Failed to revoke token");
      }

      return true;
    } catch (error) {
      console.error("Error revoking token:", error);
      return false;
    }
  };

  const handleLogout = async ({
    redirectToLogoutEndpoint = false
  }: {
    redirectToLogoutEndpoint?: boolean;
  } = {}) => {
    // Single-product users have no product switcher — treat Exit as full logout
    const shouldFullyLogout = redirectToLogoutEndpoint || hasSingleProduct;

    try {
      // Revoke token with access token and refresh token
      await revokeToken(ACCESS_TOKEN, "access_token");
      await revokeToken(REFRESH_TOKEN, "refresh_token");
      // Construct logout URL with query parameters
      const url = new URL(`${BACKEND_BASE_URL}/xsm-api-sso/v2/proxy/oidc/logout`);
      const queryParams = new URLSearchParams({
        post_logout_redirect_uri: shouldFullyLogout
          ? COMMON_CALLBACK_URL
          : redirectURL || "",
        "XSM-Domain": domain || ""
      });
      queryParams.set("id_token_hint", ID_TOKEN);

      url.search = queryParams.toString();

      //delete localStorage and Cookie
      localStorage.removeItem(`${defaultSubDomain}_access_token`);
      localStorage.removeItem(`${defaultSubDomain}_id_token`);
      localStorage.removeItem(`${defaultSubDomain}_refresh_token`);
      localStorage.removeItem(`${defaultSubDomain}_expires_in`);
      localStorage.removeItem("admin_accounts");
      localStorage.clear();
      deleteCookie("xsm_user_details");
      deleteCookie("xsm_user_domain");
      deleteCookie("xsm_user_email");
      deleteCookie("xsm_user_organization");
      deleteCookie("xsm_user_id");
      deleteCookie("xsm_admin_access");

      //delete auth token and clear out cookie
      setAuthToken();

      // Redirect to logout endpoint for full logout / single-product users;
      // otherwise return to the common product selection page
      if (subDomain !== COMMON_SUB_DOMAIN && !shouldFullyLogout) {
        window.open(COMMON_CALLBACK_URL, "_self");
      } else {
        window.location.assign(url.toString());
      }
    } catch (error) {
      dispatch(showErrorMessage("Failed to logout"));
      navigate("/");
    }
  };

  const handleAccountSettings = () => {
    navigate("/dashboard/account-settings");
  };

  return {
    fetchLoggedTokenInfo,
    handleLogout,
    loggedUser,
    handleAccountSettings,
    hasSingleProduct
  };
};
